import type { Project } from "@/interfaces/project";
import formatTime from "@/utils/formatTime";
import clockIcon from "@/assets/icons/clock.svg";
import Card from "@/components/ui/Card";
import Badge from "@/components/ui/Badge";
import Icon from "@/components/ui/Icon";

interface TestProjectCardProps {
  project: Project & { username?: string };
  variant?: "default" | "homepage";
}

// The gallery card. The whole card is clickable via a stretched link on the
// title - nesting the author/demo/repo anchors inside an outer <a> is invalid
// HTML and produced a hydration error.
export default function TestProjectCard({ project }: TestProjectCardProps) {
  const linkClasses =
    "border-line bg-surface hover:bg-goldenrod text-ink relative z-10 flex flex-1 items-center justify-center gap-1.5 border-2 px-3 py-1.5 font-display text-caption font-bold transition-colors";
  const disabledClasses =
    "border-line/30 bg-surface-alt text-ink-soft relative z-10 flex flex-1 items-center justify-center gap-1.5 border-2 px-3 py-1.5 font-display text-caption font-bold";

  return (
    <Card
      interactive
      className={`relative ${project.high_quality ? "border-goldenrod shadow-raised" : ""}`}
      media={
        project.screenshot ? (
          <img
            src={project.screenshot}
            alt={project.title ?? "Project screenshot"}
            className="h-32 w-full object-cover"
          />
        ) : (
          <div className="bg-surface-alt h-32 w-full" />
        )
      }
    >
      <div className="flex items-start justify-between gap-3">
        <h2 className="font-display text-heading text-ink min-w-0 leading-tight font-bold wrap-anywhere">
          {/* Stretched link: covers the card without nesting interactive elements. */}
          <a
            href={`/projects/${project.id}`}
            className="after:absolute after:inset-0 after:content-['']"
          >
            {project.title}
          </a>
        </h2>
        <div className="text-ink-muted flex shrink-0 items-center gap-1.5">
          <Icon src={clockIcon} label="Time logged" className="h-4 w-4" />
          <span className="font-display text-body font-bold">
            {formatTime(project.reported_seconds)}
          </span>
        </div>
      </div>

      {project.username && (
        <a
          href={`/users/${project.user_id}`}
          className="text-caption text-ink-soft relative z-10 mt-0.5 self-start hover:underline"
        >
          by {project.username}
        </a>
      )}

      {project.desc && (
        <p className="text-body text-ink-muted mt-2 line-clamp-3 wrap-break-word">
          {project.desc}
        </p>
      )}

      {project.high_quality && (
        <div className="mt-3">
          <Badge tone="brand">🎫 Golden ticket winner</Badge>
        </div>
      )}

      <div className="mt-auto flex flex-wrap gap-2 pt-4">
        {project.demo_link ? (
          <a
            href={project.demo_link}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClasses}
          >
            Demo
          </a>
        ) : (
          <span className={disabledClasses}>No demo</span>
        )}

        {project.repo_link ? (
          <a
            href={project.repo_link}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClasses}
          >
            Repository
          </a>
        ) : (
          <span className={disabledClasses}>No repository</span>
        )}
      </div>
    </Card>
  );
}
